import { DayOfWeek, Tariff } from "../../../types/tariff";
import {
  ContextLabels,
  getActivationFeeFromTariff,
  getBlockingFeeCapFromTariff,
  getBlockingFeeFromTariff,
  getDurationFromTariff,
  getEnergyFeeFromTariff,
  getTariffType,
  isFreeGroup,
} from "../../../utils/tariff";
import { parseHourlyForm } from "../GroupEdit/components/TariffEditor/utils";

const formatDuration = (seconds: number | null): string => {
  if (!seconds) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const FeeRow = ({
  label,
  value,
  unit,
  note,
}: {
  label: string;
  value: string | null;
  unit: string;
  note?: string;
}) => (
  <div className="group-tariff-row">
    <span className="flex align-items-center gap-2">
      {label}
      {note && <small className="text-color-secondary">({note})</small>}
    </span>
    <span>{value ? `${value} ${unit}` : "—"}</span>
  </div>
);

const PeriodSection = ({
  icon,
  label,
  duration,
  accentColor,
  tariff,
  energyLabel,
  blockingLabel,
  capLabel,
}: {
  icon: string;
  label: string;
  duration?: string;
  accentColor: string;
  tariff: Tariff;
  energyLabel: string;
  blockingLabel: string;
  capLabel: string;
}) => {
  const activation = getActivationFeeFromTariff(tariff);
  const energy = getEnergyFeeFromTariff(tariff, energyLabel);
  const blocking = getBlockingFeeFromTariff(tariff, blockingLabel);
  const cap = getBlockingFeeCapFromTariff(tariff, capLabel);

  return (
    <div className="group-tariff-period">
      <div className="group-tariff-period-header">
        <i className={icon} style={{ color: accentColor }} />
        <span>{label}</span>
        {duration && (
          <span className="group-tariff-period-duration">{duration}</span>
        )}
      </div>
      <div className="flex flex-column gap-2 mt-3">
        <FeeRow label="Energy price" value={energy.price} unit={energy.unit} />
        <FeeRow
          label="Blocking fee"
          value={blocking.price}
          unit={blocking.unit}
          note={blocking.restrictions?.minDuration
            ? `starts after ${formatDuration(blocking.restrictions.minDuration)}`
            : undefined}
        />
        <FeeRow
          label="Blocking fee cap"
          value={cap.price}
          unit={cap.unit}
          note={cap.restrictions?.minDuration
            ? `applies after ${formatDuration(cap.restrictions.minDuration)}`
            : undefined}
        />
        <FeeRow label="Activation fee" value={activation.price} unit={activation.unit} />
      </div>
    </div>
  );
};

// ─── Hourly helpers ───────────────────────────────────────────────────────────

const DAY_LABELS: Record<DayOfWeek, string> = {
  MONDAY: "Mon",
  TUESDAY: "Tue",
  WEDNESDAY: "Wed",
  THURSDAY: "Thu",
  FRIDAY: "Fri",
  SATURDAY: "Sat",
  SUNDAY: "Sun",
};

const HourlyBreakdown = ({ tariff }: { tariff: Tariff }) => {
  const { windows } = parseHourlyForm(tariff);

  return (
    <div className="flex flex-column gap-3">
      {/* Base tariff */}
      <PeriodSection
        icon="pi pi-tag"
        label="Base"
        accentColor="#225349"
        tariff={tariff}
        energyLabel={ContextLabels.enrgyPrice}
        blockingLabel={ContextLabels.blockingFee}
        capLabel={ContextLabels.blockingFeeCap}
      />

      {/* Windows */}
      {windows.length > 0 && (
        <div className="flex flex-column gap-2">
          {windows.map((w, i) => (
            <div key={i} className="group-tariff-period">
              <div className="group-tariff-period-header">
                <i className="pi pi-clock" style={{ color: "#225349" }} />
                <span>Window {i + 1}</span>
                <span className="group-tariff-period-duration">
                  {w.startTime} – {w.endTime}
                </span>
                {w.weekdays.length > 0 && (
                  <span className="group-tariff-period-duration group-tariff-period-duration--inline">
                    {w.weekdays.map((d) => DAY_LABELS[d as DayOfWeek]).join(", ")}
                  </span>
                )}
              </div>
              <div className="flex flex-column gap-2 mt-3">
                <FeeRow label="Energy price" value={w.energyPrice.toFixed(2)} unit="€/kWh" />
                <FeeRow
                  label="Blocking fee"
                  value={w.blockingFeePrice.toFixed(2)}
                  unit="€/h"
                  note={w.blockingFeeAfterMin > 0
                    ? `starts after ${formatDuration(w.blockingFeeAfterMin * 60)}`
                    : undefined}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── TariffBreakdown ──────────────────────────────────────────────────────────

export const TariffBreakdown = ({ tariff }: { tariff: Tariff }) => {
  try {
    const tariffType = getTariffType(tariff);

    if (!tariffType && isFreeGroup(tariff)) {
      return (
        <div className="group-tariff-body">
          <div className="group-tariff-period">
            <div className="group-tariff-period-header">
              <i className="pi pi-gift" style={{ color: "#225349" }} />
              <span>Free</span>
              <span className="group-tariff-period-duration">€0.00/kWh</span>
            </div>
            <div className="flex flex-column gap-2 mt-3">
              <FeeRow label="Energy price" value="0.00" unit="€/kWh" />
              <FeeRow label="Blocking fee" value="0.00" unit="€/h" />
              <FeeRow label="Activation fee" value="0.00" unit="€" />
            </div>
          </div>
        </div>
      );
    }

    if (tariffType === "HOURLY") {
      return (
        <div className="group-tariff-body">
          <HourlyBreakdown tariff={tariff} />
        </div>
      );
    }

    const isDayNight = tariffType === "DAY_NIGHT";

    return (
      <div className="group-tariff-body">
        {isDayNight ? (
          <div className="flex flex-column gap-3">
            <PeriodSection
              icon="pi pi-sun"
              label="Day"
              accentColor="#f59e0b"
              duration={getDurationFromTariff(tariff, ContextLabels.enrgyPriceDay)}
              tariff={tariff}
              energyLabel={ContextLabels.enrgyPriceDay}
              blockingLabel={ContextLabels.blockingFeeDay}
              capLabel={ContextLabels.blockingFeeCapDay}
            />
            <PeriodSection
              icon="pi pi-moon"
              label="Night"
              accentColor="#6366f1"
              duration={getDurationFromTariff(tariff, ContextLabels.enrgyPriceNight)}
              tariff={tariff}
              energyLabel={ContextLabels.enrgyPriceNight}
              blockingLabel={ContextLabels.blockingFeeNight}
              capLabel={ContextLabels.blockingFeeCapNight}
            />
          </div>
        ) : (
          <PeriodSection
            icon="pi pi-tag"
            label="Static"
            accentColor="#225349"
            tariff={tariff}
            energyLabel={ContextLabels.enrgyPrice}
            blockingLabel={ContextLabels.blockingFee}
            capLabel={ContextLabels.blockingFeeCap}
          />
        )}
      </div>
    );
  } catch (e) {
    console.error("Failed to parse tariff breakdown", e);
    return (
      <div className="group-tariff-empty">
        Tariff data could not be displayed.
      </div>
    );
  }
};
